'use strict';

const SecurityServiceImpl = require('./SecurityServiceImpl'),
    {t} = require('services/i18nextWrapper'),
    ContentUtils = require('utils/ContentUtils'),
    NavigationUtils = require('utils/NavigationUtils'),
    DashboardSpinnerEl = require('views/Dashboard/DashboardSpinnerHolder'),
    CustomDialog = require('components/Commons/Dialog/CustomDialogHolder'),
    apiAppUrl = process.env.URL_API,
    get = require('lodash/object').get,
    {filter} = require('lodash/collection'),
    KEY_COUNTRY = 'cache_current_country',
    KEY_CURRENCY = 'cache_current_currency',
    KEY_CURRENCY_DIGITS = 'cache_current_currency_digits',
    KEY_APPLICANT_USER_LOGGED = 'cache_applicant_user_logged',
    KEY_APPLICANT_IS_ACTIVE  = 'cache_applicant_is_active',
    KEY_APPLICANT_LOGO_SRC_LOGGED = 'cache_applicant_logo_src_logged',
    KEY_APPLICANT_USER_ID_LOGGED = 'cache_applicant_user_id_logged',
    KEY_ROLES_USER_LOGGED = 'cache_roles_user_logged',
    KEY_PERMISSIONS = 'cache_permissions',
    KEY_USER_PERMISSIONS = 'cache_user_permissions',
    KEY_DRAFT = 'cache_draft';

const auth = {

    /**
     * Log the given user.
     * @param {String} userName
     * @param {String} password
     */
    login(userName, password, texts, onSuccess, onFailure) {
        const self = this;
        SecurityServiceImpl.login(userName, password,
            function () {
                const token = this.getResponseHeader('X-AUTH-TOKEN');
                self._saveToken(token);
                self._setCurrentUser(userName);
                let response;
                SecurityServiceImpl.getCurrentApplicant(token, function () {
                    try {
                        response = JSON.parse(this.responseText);
                        self.setCurrentUserRoles(response.roles);
                        self.setCurrentApplicant(response.applicant.name);
                        self.setCurrentApplicantLogoSrc(response.applicant.logoSrc);
                        self.setApplicantIsActive(response.applicant.status);
                        self.setCurrentApplicantId(response.applicant.id);
                        self._setCurrentUserName(response.username);
                    } catch (err) {
                        console.error('ERROR when try to login: ' + err);
                    } finally {
                        if (onSuccess) {
                            onSuccess(userName, token, response.applicant.id, texts);
                        }
                    }
                 });
            }, function () {
                if (onFailure) {
                    onFailure({message: JSON.parse(this.responseText)});
                }
            });
    },

    loginFromSignUp(token, username) {
        this._saveToken(token);
        this._setCurrentUser(username);
        const self = this;
        let response;
        SecurityServiceImpl.getCurrentApplicant(token, function () {
            try {
                response = JSON.parse(this.responseText);
                self.setCurrentUserRoles(response.roles);
                self.setCurrentApplicant(response.applicant.name);
                self.setCurrentApplicantLogoSrc(response.applicant.logoSrc);
                self.setApplicantIsActive(response.applicant.status);
                self.setCurrentApplicantId(response.applicant.id);
                self._setCurrentUserName(response.username);
            } catch (err) {
                console.error('ERROR when try to login: ' + err);
            }
         });
    },

    /**
     * Clean content related the current user session and redirect to login page.
     */
    logout() {
        this._resetAuthToken();
        this._setCurrentUser('');
        this.setCurrentApplicantId('');
        this.setCurrentApplicantLogoSrc('');
        this.setCurrentApplicant('');
        this.setCurrentUserRoles('');
        this._setCurrentUserName('');
        window.location = apiAppUrl + '/#/login';
    },

    /**
     * Retrieve if the current user are already logged or not.
     * @return {Boolean}
     */
    loggedIn() {
        return !!this.getCurrentUser();
    },

    /**
     * Retrieve the current user session from the cookie.
     * @returns {String}
     */
    getCurrentUser() {
        return ContentUtils.getSession('userSession');
    },

    /**
     * Retrieve the current user session from the cookie.
     * @returns {String}
     */
    getCurrentUserName() {
        return ContentUtils.getSession('userNameSession');
    },

    getCurrentApplicant() {
        return ContentUtils.getSession(KEY_APPLICANT_USER_LOGGED);
    },

    getPermissions() {
        return ContentUtils.getSession(KEY_PERMISSIONS);
    },

    getUserPermissions() {
        return ContentUtils.getSession(KEY_USER_PERMISSIONS);
    },

    getCurrentApplicantId() {
        return ContentUtils.getSession(KEY_APPLICANT_USER_ID_LOGGED);
    },

    getCurrentCountry() {
        return ContentUtils.getSession(KEY_COUNTRY);
    },

    getCurrentCurrency() {
        return ContentUtils.getLocal(KEY_CURRENCY);
    },

    getCurrentCurrencyDigits() {
        return ContentUtils.getLocal(KEY_CURRENCY_DIGITS);
    },


    /**
     * Retrieve the current session token.
     * @return {String}
     */
    getToken() {
        return ContentUtils.getSession('X-Auth-Token');
    },

    /**
     * Save the current user name of the logged user.
     * @param {String) username
     * @private
     */
    _setCurrentUser(username) {
        ContentUtils.saveSession('userSession', username);
    },

    /**
     * Save the current user name of the logged user.
     * @param {String) username
     * @private
     */
    _setCurrentUserName(username) {
        ContentUtils.saveSession('userNameSession', username);
    },

    setCurrentUserRoles(roles) {
        ContentUtils.saveSession(KEY_ROLES_USER_LOGGED, roles);
    },

    setCurrentApplicant(applicant) {
        ContentUtils.saveSession(KEY_APPLICANT_USER_LOGGED, applicant);
    },

    setApplicantIsActive(applicantStatus) {
        ContentUtils.saveSession(KEY_APPLICANT_IS_ACTIVE, 'ACTIVE' === applicantStatus);
    },

    isApplicantIsActive() {
        return ContentUtils.getSession(KEY_APPLICANT_IS_ACTIVE);
    },

    setCurrentApplicantLogoSrc(logoSrc) {
        ContentUtils.saveSession(KEY_APPLICANT_LOGO_SRC_LOGGED, logoSrc);
    },

    getCurrentApplicantLogoSrc() {
        return ContentUtils.getSession(KEY_APPLICANT_LOGO_SRC_LOGGED) || '/assets/images/empty_profile.png';
    },

    setCurrentApplicantId(applicant) {
        ContentUtils.saveSession(KEY_APPLICANT_USER_ID_LOGGED, applicant);
    },

    setPermissions(permissions) {
        ContentUtils.saveSession(KEY_PERMISSIONS, permissions);
    },

    setUserPermissions(userPermissions) {
        ContentUtils.saveSession(KEY_USER_PERMISSIONS, userPermissions);
    },

    /**
     * Save token in the current session.
     * @param {String} token
     * @private
     */
    _saveToken(token) {
        ContentUtils.saveSession('X-Auth-Token', token);
    },

    /**
     * Clean the current session token.
     * @private
     */
    _resetAuthToken() {
        ContentUtils.saveSession('X-Auth-Token', '');
    }
};

let triggerChangePageAnyWay = false;

let Authentication = {
    statics: {

        willTransitionTo() {
            if (!auth.loggedIn()) {
                window.location = apiAppUrl + '/#/login';
            }
        },

        willTransitionFrom(transition, context) {
            let nextPath = transition.path;

            if (isCurrentPageDashboardView(context)) {
                if (triggerChangePageAnyWay) {
                    nextPath = nextPath.substring(1);
                    NavigationUtils.goTo(nextPath);
                    triggerChangePageAnyWay = false;
                    DashboardSpinnerEl.hide();
                } else {
                    if (existPendingOperationToSaveOnDashboard()) {
                        transition.abort();
                        CustomDialog.showWarningConfirmation(t('warning'), t('notification_warning_leave_dashboard_before_auto_saved'),
                            // CONFIRM
                            () => {
                                // remove all '/' to be existis
                                nextPath = nextPath.substring(1);
                                triggerChangePageAnyWay = true;
                                NavigationUtils.goTo(nextPath);
                            },
                            t('button_label_continue'),
                            t('button_label_cancel'),
                            // CANCEL
                            () => {
                                triggerChangePageAnyWay = false;
                            });
                    }
                }
            }
        }
    }
};

function existPendingOperationToSaveOnDashboard() {
    return get(_getPendingOperations(), 'length', 0) > 0;
}

function isCurrentPageDashboardView(context) {
    return context && context.state && (context.state.hasOwnProperty('immCurrentLayout') || context.state.hasOwnProperty('rangeOfDate'));
}

/**
 * NOTE:
 * The following methods (_getDraft & _getPendingOperations) are re-defined here,
 * because the package module dependency of current version
 * of Webpack don't allow cyclic dependency between modules.
 */

function _getDraft() {
    const draft = ContentUtils.getSession(KEY_DRAFT);
    if (!draft || Array.isArray(draft)) {
        return [];
    }
    return ContentUtils.getSession(KEY_DRAFT);
}

function _getPendingOperations() {
    return filter(_getDraft().operations, function (operation) {
            return operation.isSaved === false;
        }) || [];
}

// Expose for component mixing
exports.Authentication = Authentication;

// Expose api auth.
exports.auth = auth;



/** WEBPACK FOOTER **
 ** ./src/services/Security.js
 **/